/**
 * Facility entity adapter (round 13 D9).
 *
 * The site half of P371's narrow slice. A facility could be created by
 * CSV import or by API and then never looked at -- so the import
 * panel's "See the rows you imported" had nowhere to land, and the
 * concentration the MVB resource register reports ("Riyadh 69.2% of
 * the restoration set") named a place the product could not show.
 *
 * `is_recovery_site` is the one flag that changes an answer: a site
 * everything runs in AND recovers to is a single point of failure that
 * the architecture standards flag, and it is worth reading at a glance.
 */
import { useTranslation } from "react-i18next";

import type { AdminColumn, EntityAdapter, FormField } from "./types";

/** The closed vocabulary the database CHECK enforces
 * (`FACILITY_TYPES` in `shared/db/entities.py`). The VALUES are the
 * contract; the words a reader sees come from the catalogue, because
 * an adapter that hard-codes its cells is a screen no translator can
 * reach -- the defect the breadcrumb carried through two i18n sweeps. */
const FACILITY_TYPES = ["data_centre", "dr_site", "office", "branch"] as const;

function FacilityTypeLabel({ value }: { value: string | null }) {
  const { t } = useTranslation();
  if (!value) return null;
  return (
    <>
      {t(`modules.foundation.admin.facilities.type.${value}`, {
        defaultValue: value,
      })}
    </>
  );
}

function RecoverySitePill({ code }: { code: string }) {
  const { t } = useTranslation();
  return (
    <span
      data-testid={`facility-recovery-${code}`}
      className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800"
    >
      {t("modules.foundation.admin.facilities.isRecoverySite")}
    </span>
  );
}

export interface Facility {
  id: string;
  code: string;
  name: string;
  description: string | null;
  facility_type: string | null;
  location: string | null;
  is_recovery_site: boolean;
  created_at: string;
  updated_at: string;
}

export interface FacilityIn {
  code: string;
  name: string;
  description: string | null;
  facility_type: string | null;
  location: string | null;
  is_recovery_site: boolean;
}

export type FacilityPatch = Partial<FacilityIn>;

const columns: AdminColumn<Facility>[] = [
  {
    key: "code",
    label: "Code",
    sortValue: (r) => r.code,
    render: (r) => <span className="font-mono text-xs text-slate-700">{r.code}</span>,
  },
  {
    key: "name",
    label: "Name",
    sortValue: (r) => r.name,
    render: (r) => <span className="font-medium text-slate-900">{r.name}</span>,
  },
  {
    key: "facility_type",
    label: "Type",
    sortValue: (r) => r.facility_type ?? "",
    render: (r) => (
      <span className="text-xs text-slate-600">
        <FacilityTypeLabel value={r.facility_type} />
      </span>
    ),
  },
  {
    key: "location",
    label: "Location",
    sortValue: (r) => r.location ?? "",
    render: (r) => <span className="text-xs text-slate-600">{r.location ?? ""}</span>,
  },
  {
    key: "is_recovery_site",
    label: "Recovery site",
    render: (r) =>
      r.is_recovery_site ? (
        <RecoverySitePill code={r.code} />
      ) : (
        <span className="text-[11px] text-slate-400">{"--"}</span>
      ),
  },
];

const formFields: FormField<FacilityIn>[] = [
  { kind: "text", key: "code", label: "Code", required: true, maxLength: 64 },
  { kind: "text", key: "name", label: "Name", required: true, maxLength: 255 },
  { kind: "textarea", key: "description", label: "Description", rows: 3 },
  {
    kind: "select",
    key: "facility_type",
    label: "Type",
    /* The drawer's option labels are data rather than JSX children, so
       they stay plain here; the list cell above is what a reader
       scans, and that one reads the catalogue. */
    options: FACILITY_TYPES.map((v) => ({
      value: v,
      label: v.replace(/_/g, " "),
    })),
  },
  { kind: "text", key: "location", label: "Location", maxLength: 255 },
  { kind: "bool", key: "is_recovery_site", label: "Is a recovery site" },
];

export const facilityAdapter: EntityAdapter<Facility, FacilityIn, FacilityPatch> = {
  i18nPrefix: "modules.foundation.admin.facilities",
  importKind: "facility",
  endpoint: "/v1/admin/foundation/facilities",
  queryKey: ["foundation-admin", "facilities"] as const,
  columns,
  defaultSort: { key: "code", dir: "asc" },
  rowKey: (r) => r.id,
  rowSearchHaystack: (r) =>
    `${r.code} ${r.name} ${r.location ?? ""} ${r.facility_type ?? ""}`,
  formFields,
  formInitial: () => ({
    code: "",
    name: "",
    description: null,
    facility_type: null,
    location: null,
    is_recovery_site: false,
  }),
  rowToPatch: (r) => ({
    code: r.code,
    name: r.name,
    description: r.description,
    facility_type: r.facility_type,
    location: r.location,
    is_recovery_site: r.is_recovery_site,
  }),
};
